/* Copyright (c) 2020-present CloudIO Inc */

import { Theme, InputBase, IconButton, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useStateXAtom } from '@cloudio-saas/statex';
import React, { useRef } from 'react';
import WaveDots from 'utils/WaveDots';
import { useStoreBusy, useStoreDirty } from 'store/useAppStore';
import Icon from 'icons/Icon';
import ExpandCollapseIcon from 'layout/Sidebar/panels/ExpandCollapseIcon';
import { PageView, sidebarPageAccessFilterAtom } from '../../atoms';
import { useIoPageAccessVStore, useUserPagesStore } from 'store/utils/utils';
import { isViewOnlyDeveloper } from 'root/useUser';
import { usePlatform } from 'platform/PlatformHooks';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

export default function PagesAccessHeader({
  setView,
  appUid,
}: {
  appUid: string;
  setView: (_view: PageView) => void;
}): React.ReactElement {
  const store = useIoPageAccessVStore(appUid);
  const dirty = useStoreDirty(store);
  const busy = useStoreBusy(store);
  const classes = useStyles();
  const [search, setSearch] = useStateXAtom(sidebarPageAccessFilterAtom);
  const ref = useRef<HTMLInputElement>(null);
  const platform = usePlatform();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);
  const pageStore = useUserPagesStore();

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
  }

  return (
    <div className={classes.root}>
      <IconButton
        data-testid="back-button"
        onClick={() => {
          store.reset();
          setView('list');
        }}
        className={classes.iconButton}
        aria-label="Back"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          Back
        </span>
        <Icon iconName="chevron-left" />
      </IconButton>

      <InputBase
        inputRef={ref}
        className={classes.input}
        placeholder="Filter Roles"
        inputProps={{ 'aria-label': 'filter roles' }}
        value={search ?? ''}
        onChange={onChange}
        autoFocus
      />

      {dirty ? (
        <Divider className={classes.divider} orientation="vertical" />
      ) : null}
      {dirty ? (
        <IconButton
          data-testid="save-access"
          disabled={busy || isViewOnlyUser}
          color="primary"
          aria-label="Save"
          onClick={async () => {
            if (busy) return;
            const r = await store.save({
              feedback: '#{count} Page Access updated successfully!',
            });
            await pageStore.refresh();
            if (r) {
              setView('list');
            }
          }}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Save
          </span>
          {busy ? (
            <WaveDots active />
          ) : (
            <Icon color="primary" iconName="check" />
          )}
        </IconButton>
      ) : null}
      <ExpandCollapseIcon />
    </div>
  );
}
